import React from 'react';
import { Key, Shuffle } from 'lucide-react';

/**
 * Секция генератора паролей
 */
const PasswordGeneratorSection = ({
  formData,
  handleInputChange,
  errors
}) => {
  return (
    <div className={`mb-6 ${formData.autoGenerate ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'} p-4 rounded-lg border`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-medium text-gray-900 dark:text-white flex items-center">
          <Key className={`w-5 h-5 mr-2 ${formData.autoGenerate ? 'text-green-500' : 'text-blue-500'}`} />
          Генератор паролей
        </h3>
        
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="autoGenerate"
            checked={formData.autoGenerate}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Использовать генератор
          </span>
        </label>
      </div>

      {formData.autoGenerate && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Длина пароля */}
            <div>
              <label htmlFor="passwordLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Длина пароля
              </label>
              <div className="flex items-center">
                <input
                  type="range"
                  id="passwordLengthRange"
                  name="passwordLength"
                  min="6"
                  max="64"
                  value={formData.passwordLength}
                  onChange={handleInputChange}
                  className="w-full mr-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
                />
                <input
                  type="number"
                  id="passwordLength"
                  name="passwordLength"
                  value={formData.passwordLength}
                  onChange={handleInputChange}
                  min="6"
                  max="128"
                  className={`form-input w-16 ${errors.passwordLength ? 'input-error' : ''}`}
                />
              </div>
              {errors.passwordLength && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.passwordLength}
                </p>
              )}
            </div>
            
            {/* Настройки сохранения истории */}
            <div>
              <label htmlFor="saveHistory" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Настройки истории
              </label>
              <div>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    name="saveHistory"
                    checked={formData.saveHistory}
                    onChange={handleInputChange}
                    className="form-checkbox"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Сохранять в историю
                  </span>
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Результаты будут сохранены на этом устройстве
              </p>
            </div>
          </div>
          
          {/* Наборы символов */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Используемые символы
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="includeLowercase"
                  checked={formData.includeLowercase}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Строчные буквы
                </span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="includeUppercase"
                  checked={formData.includeUppercase}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Заглавные буквы
                </span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="includeNumbers"
                  checked={formData.includeNumbers}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Цифры
                </span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="includeSymbols"
                  checked={formData.includeSymbols}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Спецсимволы
                </span>
              </label>
            </div>
            {errors.characterSets && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.characterSets}
              </p>
            )}
          </div>
          
          {/* Предпросмотр пароля */}
          <div className="bg-green-100 dark:bg-green-900/40 p-3 rounded border border-green-200 dark:border-green-800 flex items-center">
            <Shuffle className="w-5 h-5 text-green-600 dark:text-green-400 mr-3" />
            <div>
              <div className="text-xs text-green-700 dark:text-green-300 mb-1">Предпросмотр (пример пароля)</div>
              <div className="font-mono text-sm text-green-900 dark:text-green-100">
                {generatePasswordPreview(formData)}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {!formData.autoGenerate && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Включите генератор для автоматического создания надежных паролей перед хешированием. 
          Вы сможете настроить длину и состав пароля.
        </p>
      )}
    </div>
  );
};

// Вспомогательная функция для генерации примера пароля
const generatePasswordPreview = (formData) => {
  const { 
    passwordLength, 
    includeLowercase, 
    includeUppercase, 
    includeNumbers, 
    includeSymbols 
  } = formData;
  
  let charset = '';
  if (includeLowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
  if (includeUppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  if (includeNumbers) charset += '0123456789';
  if (includeSymbols) charset += '!@#$%^&*()_+-=[]{}|;:,.<>?';

  // Если ничего не выбрано, используем буквы и цифры по умолчанию
  if (charset === '') charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

  let password = '';
  const length = parseInt(passwordLength, 10) || 12;
  
  // Генерируем случайный пароль
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    password += charset[randomIndex];
  }

  return password;
};

export default PasswordGeneratorSection;